/**
 * Speaker profiles -- group 05 (low-resonance cabinets and exotic diaphragms).
 *
 * The through-line in this group is what each designer does about stored
 * energy: an infinite-slope crossover, a tapered absorber behind every driver,
 * a billet aluminium shell, a sand-damped composite, a cast marble hourglass,
 * a first-order network, or a sealed box with an iron-free motor. Those choices
 * show up as different crossover slopes, different cabinet resonance sets and
 * different voicing offsets, which is what the responseTilt array carries.
 * Sensitivity, impedance and bass extension come from published data where it
 * exists; the anechoic curve, directivity and distortion structure are modelled.
 */

import type { SpeakerProfile } from '../../types/hardware';
import { HARMONIC_SHAPES, makeSpeaker, usd } from '../kit';

export const SPEAKERS_05: SpeakerProfile[] = [
  makeSpeaker({
    mfr: 'Joseph Audio',
    model: 'Pulsar2 Graphene',
    price: usd(9999, 'APPROXIMATE', 2023, 'Approximate US retail per pair; not verified against a current price list.'),
    year: 2021,
    country: 'United States',
    speakerType: 'standmount',
    enclosure: 'ported',
    drivers: ['dynamic'],
    driverCount: 2,
    wayCount: 2,
    sensitivityDb: 86,
    nominalOhms: 8,
    minOhms: 6.2,
    bass: [42, 35],
    hfExtension: 25000,
    portTuningHz: 40,
    crossovers: [{ freqHz: 2000, slopeDbOct: 120, alignment: 'butterworth', offsetMm: 18 }],
    responseTilt: [[20, 0], [60, 0.4], [150, -0.3], [500, 0], [1200, 0.3], [2000, -0.7], [4000, 0.2], [8000, 0.4], [14000, -0.3], [20000, -1.0]],
    resonances: [[195, -34, 9], [520, -38, 12]],
    coverageAngleDeg: 120,
    recommendedWatts: [50, 200],
    dimensionsMm: [406, 229, 305],
    massKg: 14,
    thdPercent: 0.3,
    roomCoupling: 'medium',
    confidence: 'ESTIMATED',
    accent: '#7fa9c9',
    tags: ['infinite-slope', 'two-way', 'magnesium-cone', 'steep-crossover'],
    notes:
      'The defining parameter is the crossover slope. An infinite-slope network of roughly 120 dB per octave means each driver is used only over the band where it is well behaved, so the modelled overlap region is almost nonexistent and the cone breakup of the woofer never reaches the acoustic sum. The cost is a large phase rotation concentrated at the crossover point, which the minimum-phase derivation reproduces, and a narrow suckout right at 2 kHz where the two acoustic outputs meet with the steepest possible transition.',
    provenanceNote:
      'Steep infinite-slope crossover topology, two-way configuration and the ported alignment are from Joseph Audio published descriptions. Sensitivity, impedance, bass extension, dimensions and mass are ESTIMATED from the model family rather than transcribed from a datasheet, and the crossover frequency and cabinet resonances are modelled. Price is approximate.',
  }),

  makeSpeaker({
    mfr: 'Vivid Audio',
    model: 'Giya G3',
    price: usd(50000, 'APPROXIMATE', 2023, 'Approximate US retail per pair; not verified.'),
    year: 2012,
    country: 'South Africa',
    speakerType: 'floorstander',
    enclosure: 'transmission-line',
    drivers: ['dynamic'],
    driverCount: 5,
    wayCount: 4,
    sensitivityDb: 87,
    nominalOhms: 6,
    minOhms: 4.0,
    bass: [33, 29],
    hfExtension: 36000,
    crossovers: [[220, 24], [880, 24], [3500, 24]],
    responseTilt: [[20, 0], [50, 0.2], [120, -0.2], [400, 0.1], [1000, 0.3], [3000, 0.5], [7000, 0.2], [12000, 0.4], [20000, 0.6], [30000, -1.0]],
    resonances: [[260, -52, 16], [700, -56, 20]],
    coverageAngleDeg: 110,
    recommendedWatts: [50, 400],
    dimensionsMm: [1310, 380, 590],
    massKg: 42,
    thdPercent: 0.28,
    roomCoupling: 'medium',
    confidence: 'ESTIMATED',
    accent: '#69c2b4',
    tags: ['tapered-tube', 'reaction-cancelling', 'four-way', 'composite-shell'],
    notes:
      'Every driver fires into an exponentially tapered tube that terminates its rear output, and the bass drivers are mounted back to back on a common shaft so their reaction forces cancel into the shell. The modelling consequence is that cabinet resonances are pushed far below the level used for a conventional braced box, roughly 50 dB down and broad rather than 30 dB down and high-Q, so the stored-energy contribution to the response is close to negligible. The curved composite shell has no parallel internal surfaces and no flat baffle, which is why the modelled baffle step is gentle and the top octave stays flat past 20 kHz rather than showing an alloy dome breakup peak.',
    provenanceNote:
      'Tapered tube loading, reaction-cancelling bass arrangement and four-way configuration are from Vivid Audio published descriptions. Sensitivity, nominal impedance and bass extension are ESTIMATED from the model family; crossover frequencies, cabinet resonance levels, dimensions and mass are modelled or approximate rather than transcribed. Price is approximate.',
  }),

  makeSpeaker({
    mfr: 'YG Acoustics',
    model: 'Carmel 2',
    price: usd(24300, 'APPROXIMATE', 2020, 'Approximate US retail per pair; not verified against a current price list.'),
    year: 2015,
    country: 'United States',
    speakerType: 'floorstander',
    enclosure: 'ported',
    drivers: ['dynamic'],
    driverCount: 2,
    wayCount: 2,
    sensitivityDb: 87,
    nominalOhms: 4,
    minOhms: 3.4,
    bass: [35, 30],
    hfExtension: 40000,
    portTuningHz: 33,
    crossovers: [{ freqHz: 1750, slopeDbOct: 24, alignment: 'bessel', offsetMm: 12 }],
    responseTilt: [[20, 0], [60, -0.2], [200, -0.4], [700, 0], [1500, 0.2], [3000, -0.5], [6000, 0.4], [10000, 0.7], [16000, 0.9], [20000, 0.3]],
    resonances: [[310, -50, 18], [820, -54, 24]],
    coverageAngleDeg: 105,
    recommendedWatts: [50, 300],
    dimensionsMm: [960, 260, 400],
    massKg: 40,
    thdPercent: 0.25,
    roomCoupling: 'medium',
    confidence: 'ESTIMATED',
    accent: '#b9bec6',
    tags: ['billet-aluminium', 'machined-driver', 'phase-optimised-crossover', 'two-way'],
    notes:
      'Cones and domes are machined from solid aluminium rather than formed, and the crossover is optimised for relative phase as well as amplitude, so this entry uses a Bessel alignment with an explicit driver offset instead of the usual Linkwitz-Riley assumption. That choice trades a little amplitude flatness through the crossover region for a much smaller phase rotation, which the minimum-phase derivation plus the offset term reproduces as a shallower group delay step at 1.75 kHz. The metal dome pushes its first breakup mode well above the audible band, which is modelled as a flat extension to 40 kHz with a mild rise through the top two octaves rather than a soft roll-off.',
    provenanceNote:
      'In-house machined aluminium drivers and the phase-optimised crossover philosophy are from YG Acoustics published descriptions. Sensitivity, impedance, bass extension, crossover frequency, enclosure alignment, dimensions and mass are ESTIMATED or inferred from the model family; the ported alignment in particular is inferred and would need revision if the production cabinet is sealed. Price is approximate.',
  }),

  makeSpeaker({
    mfr: 'Rockport Technologies',
    model: 'Avior II',
    price: usd(36500, 'APPROXIMATE', 2021, 'Approximate US retail per pair; not verified.'),
    year: 2018,
    country: 'United States',
    speakerType: 'floorstander',
    enclosure: 'ported',
    drivers: ['beryllium-dome', 'dynamic'],
    driverCount: 4,
    wayCount: 3,
    sensitivityDb: 88,
    nominalOhms: 4,
    minOhms: 3.0,
    bass: [25, 21],
    hfExtension: 40000,
    portTuningHz: 24,
    crossovers: [[130, 24], [2200, 24]],
    responseTilt: [[20, 0.3], [45, 0.6], [110, 0.3], [300, -0.4], [900, -0.2], [2500, 0.1], [5000, -0.5], [9000, -0.2], [15000, -0.8], [20000, -1.4]],
    resonances: [[185, -50, 8], [510, -54, 11]],
    coverageAngleDeg: 100,
    recommendedWatts: [50, 600],
    dimensionsMm: [1245, 394, 622],
    massKg: 102,
    thdPercent: 0.22,
    roomCoupling: 'medium',
    confidence: 'ESTIMATED',
    accent: '#8b7d6b',
    tags: ['constrained-layer', 'composite-cabinet', 'carbon-sandwich-cone', 'low-crossover'],
    notes:
      'A very heavy composite cabinet with a damped constrained-layer wall, modelled as resonances that are both low in level and low in Q: energy is dissipated rather than reflected, so the decay is short and broad instead of ringing at a specific frequency. The low crossover point at 130 Hz keeps the midrange driver out of the region where cabinet and room modes dominate and hands the entire lower range to the sandwich-cone woofers. Voicing is a mild lower-midrange lift with a gently falling top octave, which is a different balance from the metal-dome designs in this group that rise through the treble.',
    provenanceNote:
      'Composite constrained-layer cabinet construction and carbon-sandwich cone drivers are from Rockport published descriptions. Sensitivity, impedance, bass extension, crossover points, dimensions and mass are ESTIMATED from the model family; cabinet resonance frequencies, levels and Q are modelled from the stated damping philosophy, not measured. Price is approximate.',
  }),

  makeSpeaker({
    mfr: 'Estelon',
    model: 'X Diamond Mk II',
    price: usd(59000, 'APPROXIMATE', 2022, 'Approximate US retail per pair; Estelon is priced in EUR and this figure is not verified.'),
    year: 2018,
    country: 'Estonia',
    speakerType: 'floorstander',
    enclosure: 'ported',
    drivers: ['diamond-dome', 'dynamic'],
    driverCount: 3,
    wayCount: 3,
    sensitivityDb: 88,
    nominalOhms: 6,
    minOhms: 3.2,
    bass: [20, 17],
    hfExtension: 60000,
    portTuningHz: 19,
    crossovers: [[120, 12], [1800, 12]],
    responseTilt: [[20, 0], [40, 0.4], [90, -0.3], [250, -0.5], [800, 0.2], [2000, 0.6], [4000, 0.3], [9000, 0.6], [15000, 1.0], [20000, 0.8]],
    resonances: [[240, -46, 12], [610, -50, 16]],
    coverageAngleDeg: 100,
    recommendedWatts: [50, 400],
    dimensionsMm: [1370, 380, 570],
    massKg: 78,
    thdPercent: 0.3,
    roomCoupling: 'medium',
    confidence: 'ESTIMATED',
    accent: '#d3c1e0',
    tags: ['marble-composite', 'ceramic-midrange', 'diamond-tweeter', 'shallow-crossover'],
    notes:
      'A cast marble-composite shell with an hourglass waist, so the radiating baffle area around each driver is small and curved and the modelled diffraction contribution is low even though the enclosure is large. Second-order acoustic slopes at both crossover points mean the drivers overlap over roughly an octave, which lowers the phase rotation but forces the ceramic midrange to be well behaved an octave outside its passband. The diamond dome extends usefully to 60 kHz, and the voicing carries a small upper-midrange and top-octave lift rather than the falling treble of the damped-composite designs here.',
    provenanceNote:
      'Cast composite cabinet, hourglass profile and the ceramic and diamond driver complement are from Estelon published descriptions. Sensitivity, impedance minimum, bass extension, crossover points and slopes, dimensions and mass are ESTIMATED or inferred; Estelon does not publish a full anechoic specification. Price is an approximate currency conversion.',
  }),

  makeSpeaker({
    mfr: 'Marten',
    model: 'Parker Trio',
    price: usd(24000, 'APPROXIMATE', 2022, 'Approximate US retail per pair; Marten is priced in EUR and this figure is not verified.'),
    year: 2019,
    country: 'Sweden',
    speakerType: 'floorstander',
    enclosure: 'ported',
    drivers: ['dynamic'],
    driverCount: 3,
    wayCount: 3,
    sensitivityDb: 89,
    nominalOhms: 6,
    minOhms: 4.4,
    bass: [26, 22],
    hfExtension: 30000,
    portTuningHz: 25,
    crossovers: [
      { freqHz: 400, slopeDbOct: 6, alignment: 'first-order' },
      { freqHz: 2500, slopeDbOct: 6, alignment: 'first-order' },
    ],
    responseTilt: [[20, 0.2], [50, 0.5], [120, 0.2], [400, -0.2], [1200, -0.3], [3000, -0.6], [6000, -0.4], [11000, -0.2], [16000, 0.3], [20000, -0.5]],
    resonances: [[220, -42, 10], [560, -46, 13]],
    coverageAngleDeg: 115,
    recommendedWatts: [50, 250],
    dimensionsMm: [1180, 250, 380],
    massKg: 45,
    thdPercent: 0.35,
    roomCoupling: 'medium',
    confidence: 'ESTIMATED',
    accent: '#9fbf8f',
    tags: ['first-order-crossover', 'ceramic-drivers', 'wide-overlap', 'three-way'],
    notes:
      'First-order acoustic slopes at both crossover points, which is the opposite design choice to the infinite-slope entry in this group and produces the opposite set of trade-offs. Each driver runs two or three octaves outside its nominal band, so the ceramic diaphragm breakup modes are only 6 dB per octave down at the point where they occur and the distortion contribution from out-of-band operation is higher, but the phase behaviour through the crossover is nearly transient-perfect and the vertical lobing is broad. The modelled voicing is a gentle downward tilt from the lower midrange to the presence region, which is characteristic of wide-overlap networks summing on the design axis.',
    provenanceNote:
      'First-order crossover philosophy and the ceramic driver complement are from Marten published descriptions. Sensitivity, impedance, bass extension, crossover frequencies, dimensions and mass are ESTIMATED from the model family rather than transcribed from a datasheet. Price is an approximate currency conversion.',
  }),

  makeSpeaker({
    mfr: 'Raidho Acoustics',
    model: 'TD1.2',
    price: usd(29000, 'APPROXIMATE', 2022, 'Approximate US retail per pair; not verified.'),
    year: 2019,
    country: 'Denmark',
    speakerType: 'standmount',
    enclosure: 'sealed',
    drivers: ['ribbon', 'dynamic'],
    driverCount: 2,
    wayCount: 2,
    sensitivityDb: 86,
    nominalOhms: 8,
    minOhms: 5.6,
    bass: [45, 38],
    hfExtension: 50000,
    crossovers: [{ freqHz: 3000, slopeDbOct: 12, alignment: 'butterworth' }],
    responseTilt: [[20, -0.5], [50, -0.3], [150, 0.3], [500, 0.1], [1500, -0.4], [3000, -0.2], [6000, 0.5], [10000, 0.9], [16000, 1.2], [20000, 1.0]],
    resonances: [[290, -44, 11], [740, -48, 15]],
    coverageAngleDeg: 100,
    recommendedWatts: [50, 200],
    dimensionsMm: [420, 240, 480],
    massKg: 21,
    thdPercent: 0.2,
    roomCoupling: 'low',
    confidence: 'ESTIMATED',
    accent: '#c5d7e8',
    tags: ['sealed-ribbon', 'diamond-coated-cone', 'sealed-box', 'low-mass-diaphragm'],
    notes:
      'A sealed two-way with a foil ribbon tweeter in a sealed rear chamber, so the low-frequency roll-off is second order and the phase and group delay below 100 Hz are far better behaved than the ported designs in this group at the price of about an octave of extension. The ribbon has essentially no moving mass and no dome breakup, which is modelled as flat output to 50 kHz with a rising top-octave voicing rather than a resonant peak. The 12 dB per octave crossover at 3 kHz is a deliberate compromise: it protects the low-mass ribbon from excursion below its resonance while keeping the network small.',
    provenanceNote:
      'Sealed enclosure, sealed-ribbon tweeter and the diamond-coated sandwich cone are from Raidho published descriptions. Sensitivity, impedance, bass extension, crossover frequency, dimensions and mass are ESTIMATED from the model family; Raidho does not publish an anechoic response. Price is approximate.',
  }),

  makeSpeaker({
    mfr: 'Borresen Acoustics',
    model: '02',
    price: usd(21000, 'APPROXIMATE', 2022, 'Approximate US retail per pair; Borresen is priced in EUR and this figure is not verified.'),
    year: 2019,
    country: 'Denmark',
    speakerType: 'floorstander',
    enclosure: 'sealed',
    drivers: ['planar-panel', 'dynamic'],
    driverCount: 3,
    wayCount: 2,
    sensitivityDb: 86,
    nominalOhms: 4,
    minOhms: 3.2,
    bass: [40, 33],
    hfExtension: 50000,
    crossovers: [{ freqHz: 2500, slopeDbOct: 12, alignment: 'butterworth', offsetMm: 24 }],
    responseTilt: [[20, -0.4], [45, 0], [120, 0.4], [350, 0], [1000, -0.5], [2500, -0.8], [5000, 0.2], [9000, 0.7], [14000, 1.0], [20000, 0.6]],
    resonances: [[270, -46, 13], [690, -50, 17]],
    coverageAngleDeg: 95,
    recommendedWatts: [50, 300],
    dimensionsMm: [1100, 230, 380],
    massKg: 38,
    thdPercent: 0.25,
    harmonicShape: HARMONIC_SHAPES.planar,
    roomCoupling: 'low',
    confidence: 'ESTIMATED',
    accent: '#8ea0c8',
    tags: ['iron-free-motor', 'planar-tweeter', 'sealed-box', 'low-inductance'],
    notes:
      'The bass and midrange motors contain no iron in the magnetic circuit, which removes the main source of flux modulation and voice-coil inductance variation with excursion. That is modelled here as a planar-weighted harmonic structure with a low, slowly decaying tail rather than the second and third harmonic pair typical of a conventional ferrite or steel motor, and as an impedance curve that does not climb steeply with frequency above the crossover. A sealed alignment with a 40 Hz corner and a nominal four ohm load with a 3.2 ohm minimum means the current demand is real even though the sensitivity is only 86 dB.',
    provenanceNote:
      'Iron-free motor construction, planar tweeter and the sealed enclosure are from Borresen published descriptions. Sensitivity, impedance, bass extension, crossover frequency, dimensions and mass are ESTIMATED from the model family. The planar-weighted distortion structure is a modelling choice justified by the stated motor topology, not a measurement. Price is an approximate currency conversion.',
  }),
];
